import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from '@/components/charts/recharts';
import { TrendingUp, Wallet, PiggyBank, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function SIPCalculator() {
  const [monthlyInvestment, setMonthlyInvestment] = useState(10000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);
  const [enableStepUp, setEnableStepUp] = useState(false);
  const [stepUpType, setStepUpType] = useState('percentage');
  const [stepUpValue, setStepUpValue] = useState(10);
  
  // Input display states for editing
  const [investmentInput, setInvestmentInput] = useState('10,000');
  const [returnInput, setReturnInput] = useState('12');
  const [periodInput, setPeriodInput] = useState('10');
  const [stepUpInput, setStepUpInput] = useState('10');
  
  // Error states
  const [errors, setErrors] = useState({});

  // Indian number formatting
  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '';
    const numStr = Math.round(num).toString();
    let lastThree = numStr.substring(numStr.length - 3);
    let otherNumbers = numStr.substring(0, numStr.length - 3);
    if (otherNumbers !== '') lastThree = ',' + lastThree;
    return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
  };

  const parseNumber = (str) => {
    if (!str) return 0;
    return parseInt(str.replace(/,/g, ''), 10) || 0;
  };

  // Input handlers
  const handleInvestmentChange = (e) => {
    const raw = e.target.value.replace(/,/g, '');
    setInvestmentInput(e.target.value);
    const num = parseInt(raw, 10);
    if (!isNaN(num) && num >= 500 && num <= 10000000) {
      setMonthlyInvestment(num);
      setErrors(prev => ({ ...prev, investment: '' }));
    } else if (raw === '') {
      setErrors(prev => ({ ...prev, investment: '' }));
    } else {
      setErrors(prev => ({ ...prev, investment: 'Enter amount between ₹500 and ₹1,00,00,000' }));
    }
  };

  const handleInvestmentBlur = () => {
    const num = parseNumber(investmentInput);
    const clamped = Math.max(500, Math.min(10000000, num || 500));
    setMonthlyInvestment(clamped);
    setInvestmentInput(formatIndianNumber(clamped));
    setErrors(prev => ({ ...prev, investment: '' }));
  };

  const handleReturnChange = (e) => {
    setReturnInput(e.target.value);
    const num = parseFloat(e.target.value);
    if (!isNaN(num) && num >= 2 && num <= 12) {
      setExpectedReturn(num);
      setErrors(prev => ({ ...prev, return: '' }));
    } else if (e.target.value === '') {
      setErrors(prev => ({ ...prev, return: '' }));
    } else {
      setErrors(prev => ({ ...prev, return: 'Return rate must be between 2% and 12%' }));
    }
  };

  const handleReturnBlur = () => {
    const num = parseFloat(returnInput) || 2;
    const clamped = Math.max(2, Math.min(12, num));
    setExpectedReturn(clamped);
    setReturnInput(clamped.toString());
    setErrors(prev => ({ ...prev, return: '' }));
  };

  const handlePeriodChange = (e) => {
    setPeriodInput(e.target.value);
    const num = parseInt(e.target.value, 10);
    if (!isNaN(num) && num >= 1 && num <= 40) {
      setTimePeriod(num);
      setErrors(prev => ({ ...prev, period: '' }));
    } else if (e.target.value === '') {
      setErrors(prev => ({ ...prev, period: '' }));
    } else {
      setErrors(prev => ({ ...prev, period: 'Time period must be between 1 and 40 years' }));
    }
  };

  const handlePeriodBlur = () => {
    const num = parseInt(periodInput, 10) || 1;
    const clamped = Math.max(1, Math.min(40, num));
    setTimePeriod(clamped);
    setPeriodInput(clamped.toString());
    setErrors(prev => ({ ...prev, period: '' }));
  };

  const handleStepUpChange = (e) => {
    setStepUpInput(e.target.value);
    const num = parseFloat(e.target.value.replace(/,/g, ''));
    if (!isNaN(num) && num >= 0) {
      setStepUpValue(num);
    }
  };

  const handleStepUpBlur = () => {
    const num = parseFloat(stepUpInput.replace(/,/g, '')) || 0;
    setStepUpValue(Math.max(0, num));
    setStepUpInput(stepUpType === 'percentage' ? num.toString() : formatIndianNumber(num));
  };

  const results = useMemo(() => {
    const monthlyRate = expectedReturn / 100 / 12;
    const months = timePeriod * 12;
    let totalInvestment = 0;
    let futureValue = 0;
    let currentMonthlyInvestment = monthlyInvestment;
    const yearlyData = [];

    for (let month = 1; month <= months; month++) {
      totalInvestment += currentMonthlyInvestment;
      futureValue = (futureValue + currentMonthlyInvestment) * (1 + monthlyRate);

      if (enableStepUp && month % 12 === 0 && month < months) {
        if (stepUpType === 'percentage') {
          currentMonthlyInvestment = currentMonthlyInvestment * (1 + stepUpValue / 100);
        } else {
          currentMonthlyInvestment = currentMonthlyInvestment + stepUpValue;
        }
      }

      if (month % 12 === 0) {
        const year = month / 12;
        yearlyData.push({
          year: `Year ${year}`,
          yearNum: year,
          investment: Math.round(totalInvestment),
          returns: Math.round(futureValue - totalInvestment),
          totalValue: Math.round(futureValue),
        });
      }
    }

    // Calculate without step-up for comparison
    let simpleInvestment = 0;
    let simpleFutureValue = 0;
    for (let month = 1; month <= months; month++) {
      simpleInvestment += monthlyInvestment;
      simpleFutureValue = (simpleFutureValue + monthlyInvestment) * (1 + monthlyRate);
    }

    const additionalGain = enableStepUp ? Math.round(futureValue - simpleFutureValue) : 0;

    return {
      futureValue: Math.round(futureValue),
      totalInvestment: Math.round(totalInvestment),
      wealthGain: Math.round(futureValue - totalInvestment),
      yearlyData,
      additionalGain,
    };
  }, [monthlyInvestment, expectedReturn, timePeriod, enableStepUp, stepUpType, stepUpValue]);

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    return `₹${formatIndianNumber(value)}`;
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4">
            SIP Calculator
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Calculate the future value of your Systematic Investment Plan and see how your wealth grows over time
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="glass-effect rounded-2xl p-8">
            <h2 className="font-heading text-2xl font-bold text-text-primary mb-6">Investment Details</h2>
            
            <div className="space-y-6">
              {/* Monthly Investment */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Monthly Investment (₹)</label>
                <input
                  type="text"
                  value={investmentInput}
                  onChange={handleInvestmentChange}
                  onBlur={handleInvestmentBlur}
                  onFocus={() => setInvestmentInput(monthlyInvestment.toString())}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.investment ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="sip-monthly-investment"
                />
                {errors.investment && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.investment}
                  </p>
                )}
                <input
                  type="range"
                  min="500"
                  max="100000"
                  step="500"
                  value={monthlyInvestment}
                  onChange={(e) => {
                    setMonthlyInvestment(parseInt(e.target.value));
                    setInvestmentInput(formatIndianNumber(parseInt(e.target.value)));
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Expected Return */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Expected Return (% p.a.) <span className="text-text-muted">Range: 2% to 12%</span></label>
                <input
                  type="text"
                  value={returnInput}
                  onChange={handleReturnChange}
                  onBlur={handleReturnBlur}
                  onFocus={() => setReturnInput(expectedReturn.toString())}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.return ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="sip-expected-return"
                />
                {errors.return && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.return}
                  </p>
                )}
                <input
                  type="range"
                  min="2"
                  max="12"
                  step="0.5"
                  value={expectedReturn}
                  onChange={(e) => {
                    setExpectedReturn(parseFloat(e.target.value));
                    setReturnInput(e.target.value);
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Time Period */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Time Period (years)</label>
                <input
                  type="text"
                  value={periodInput}
                  onChange={handlePeriodChange}
                  onBlur={handlePeriodBlur}
                  onFocus={() => setPeriodInput(timePeriod.toString())}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.period ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="sip-time-period"
                />
                {errors.period && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.period}
                  </p>
                )}
                <input
                  type="range"
                  min="1"
                  max="40"
                  value={timePeriod}
                  onChange={(e) => {
                    setTimePeriod(parseInt(e.target.value));
                    setPeriodInput(e.target.value);
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Step-Up SIP */}
              <div className="border-t border-white/10 pt-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableStepUp}
                    onChange={(e) => setEnableStepUp(e.target.checked)}
                    className="w-5 h-5 rounded border-white/10 bg-white/5 text-primary focus:ring-primary"
                    data-testid="sip-enable-stepup"
                  />
                  <span className="ml-3 text-text-primary font-medium">Enable Step-Up SIP</span>
                </label>

                {enableStepUp && (
                  <div className="mt-4 space-y-4 pl-8">
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="stepUpType"
                          value="percentage"
                          checked={stepUpType === 'percentage'}
                          onChange={() => {
                            setStepUpType('percentage');
                            setStepUpInput(stepUpValue.toString());
                          }}
                          className="w-4 h-4 text-primary"
                        />
                        <span className="ml-2 text-text-secondary">Increase by % annually</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="stepUpType"
                          value="fixed"
                          checked={stepUpType === 'fixed'}
                          onChange={() => {
                            setStepUpType('fixed');
                            setStepUpInput(formatIndianNumber(stepUpValue));
                          }}
                          className="w-4 h-4 text-primary"
                        />
                        <span className="ml-2 text-text-secondary">Increase by fixed amount</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-text-secondary text-sm mb-2">
                        {stepUpType === 'percentage' ? 'Annual Increase (%)' : 'Annual Increase (₹)'}
                      </label>
                      <input
                        type="text"
                        value={stepUpInput}
                        onChange={handleStepUpChange}
                        onBlur={handleStepUpBlur}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                        data-testid="sip-stepup-value"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="glass-effect rounded-xl p-6 text-center">
                <Wallet className="h-8 w-8 text-primary mx-auto mb-2" />
                <p className="text-text-muted text-sm">Total Investment</p>
                <p className="text-2xl font-bold text-text-primary">{formatCurrency(results.totalInvestment)}</p>
              </div>
              <div className="glass-effect rounded-xl p-6 text-center">
                <TrendingUp className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <p className="text-text-muted text-sm">Total Returns</p>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(results.wealthGain)}</p>
              </div>
              <div className="glass-effect rounded-xl p-6 text-center">
                <PiggyBank className="h-8 w-8 text-secondary mx-auto mb-2" />
                <p className="text-text-muted text-sm">Final Value</p>
                <p className="text-2xl font-bold text-secondary">{formatCurrency(results.futureValue)}</p>
              </div>
            </div>

            {/* Insight */}
            {enableStepUp && results.additionalGain > 0 && (
              <div className="glass-effect rounded-xl p-4 border-l-4 border-primary">
                <p className="text-text-secondary">
                  <span className="text-primary font-semibold">Insight:</span> By increasing your SIP by {stepUpType === 'percentage' ? `${stepUpValue}%` : `₹${formatIndianNumber(stepUpValue)}`} annually, you gain an additional <span className="text-green-400 font-bold">{formatCurrency(results.additionalGain)}</span>!
                </p>
              </div>
            )}

            {/* Chart */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Growth Over Time</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={results.yearlyData}
                    margin={{ top: 10, right: 16, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="year" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="investment" name="Investment" stroke="#94a3b8" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="totalValue" name="Total Value" stroke="#F5A623" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Year-wise Table */}
        <div className="mt-8 glass-effect rounded-2xl p-6">
          <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Year-wise Breakdown</h3>
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="sticky top-20 bg-background z-10 text-left py-3 px-4 text-text-muted font-medium">Year</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Investment</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Returns</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Total Value</th>
              </tr>
            </thead>
            <tbody>
              {results.yearlyData.map((row, index) => (
                <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-3 px-4 text-text-primary">{row.year}</td>
                  <td className="py-3 px-4 text-right text-text-secondary">{formatCurrency(row.investment)}</td>
                  <td className="py-3 px-4 text-right text-green-400">{formatCurrency(row.returns)}</td>
                  <td className="py-3 px-4 text-right text-primary font-medium">{formatCurrency(row.totalValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 glass-effect rounded-xl p-4 border border-yellow-500/30">
          <p className="text-text-muted text-sm">
            <span className="text-yellow-500 font-semibold">Disclaimer:</span> The calculations shown are based on the inputs provided and assume constant returns throughout the investment period. Actual returns may vary based on market conditions, fund performance, and other factors. These projections are for illustrative purposes only and should not be considered as financial advice. Please consult a qualified financial advisor before making investment decisions.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">Get a Personalized Investment Plan</h3>
            <p className="text-text-secondary mb-6">Let us help you create a customized SIP strategy based on your financial goals</p>
            <Link to="/contact">
              <Button className="btn-primary text-lg px-8 py-4">
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
